import { Analytics, Hub } from "aws-amplify";
import React, { createContext, FC, ReactNode, useCallback, useEffect, useState } from "react";
import { Passport, Storage } from "../../utils/Storage";
import {
    createAssistantData,
    createPassportDynaDBData,
    getAssistantData,
    getPassportDataFromDynaDB,
} from "../../services/NetworkManager";
import { RasaUtils } from "../../utils/Rasa.utils";
import { updatePassportAndWellbeingFormData } from "../../utils/passport.utils";
import { ORGANIZATION_ACCESS_URL, RASA_HOST, VERSION } from "../../../constants";
import { accessOrganization } from "../../utils/API";
import { generatePassword } from "../../utils/generatePassword";
import { Assistant } from "../../API";
import {
    signIn,
    signOut,
    getCurrentUser,
    getSession,
    isAuthenticated,
    refreshTokenIfNeeded,
} from "../../utils/cognitoAuth"; // Import refactored functions

type IUser = {
    username: string;
    token: { jwtToken: string };
};

export type IProfileMood = {
    mood: string;
    date: number;
};

interface IAuthContext {
    user: IUser | null;
    hasAccess: boolean;
    accessLink: string;
    accessErrorMessage: string;
    isAuthenticating: boolean;
    passportData: Partial<Passport>;
    assistantData: Partial<Assistant>;
    profileMoods: IProfileMood[];

    login(username: string, password: string): Promise<any>;
    logout(): Promise<any>;
    setPassportData: (data: Passport) => void;
    setAssistantData: (data: Assistant) => void;
    setProfileMoods: (data: IProfileMood[]) => void;
}

export const AuthContext = createContext<IAuthContext>({
    user: null,
    hasAccess: true,
    accessLink: "",
    accessErrorMessage: "",
    isAuthenticating: true,
    passportData: {},
    assistantData: {},
    profileMoods: [],
    logout: async () => {},
    login: async () => {},
    setPassportData: () => {},
    setAssistantData: () => {},
    setProfileMoods: () => {},
});

const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [hasAccess, setHasAccess] = useState<boolean>(true);
    const [accessLink, setAccessLink] = useState<string>("");
    const [accessErrorMessage, setAccessErrorMessage] = useState<string>("");
    const [passportData, setPassportData] = useState<Passport>();
    const [profileMoods, setProfileMoods] = useState<IProfileMood[]>([]);
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);

    /**
     * Ensures user session is restored and tokens are refreshed if necessary.
     */
    useEffect(() => {
        (async () => {
            try {
                // Check if user is already authenticated
                const validToken = await refreshTokenIfNeeded();
                if (!validToken) {
                    setIsAuthenticating(false);
                    return;
                }

                // Get authenticated user details
                const user = await getCurrentUser();
                if (user) {
                    onLoggedIn(user, { jwtToken: validToken });
                } else {
                    setIsAuthenticating(false);
                }
            } catch (error) {
                console.error("AuthProvider Error: ", error);
                setIsAuthenticating(false);
            }
        })();
    }, []);

    /**
     * Handles login logic and initializes user data.
     */
    const onLoggedIn = async (
      user: any,
      token: {
          jwtToken: string;
      }
    ) => {
        try {
            await accessOrganization(ORGANIZATION_ACCESS_URL, user.attributes.email, VERSION, token.jwtToken);
            await initUserData();
            setUser({ username: user.username, token });
            setHasAccess(true);
        } catch (error: any) {
            console.error("Error:", error);
            setIsAuthenticating(false);
            setHasAccess(false);
            setAccessErrorMessage(error.errorMessage || "There is a network error, please try again later");
            if (error.link) setAccessLink(error.link);
            await signOut();
        }
    };

    /**
     * Retrieves and stores user data from Cognito and external services.
     */
    const initUserData = useCallback(async () => {
        try {

            const session = await getSession();
            if (!session) {
                setErrorMessage("User session expired. Please sign in again.");
                return;
            }


            const user = await getCurrentUser();
            if (!user) return;

            const attrs = user.attributes;
            for (let [key, value] of Object.entries(attrs)) {
                await Storage.setValue(key, value);
            }

            let passportIdData = await getPassportDataFromDynaDB(attrs.sub);
            if (!passportIdData) {
                passportIdData = await createPassportDynaDBData(attrs.sub, generatePassword());
            }

            let assistantData = await getAssistantData(attrs.sub);
            if (!assistantData) {
                assistantData = await createAssistantData(attrs.sub);
            }

            await Storage.setValue("password", passportIdData.password);
            await Storage.setValue("passport_url", passportIdData.passport_url);
            await Storage.setValue("passportId", attrs.sub);

            const updatedPassportData = updatePassportAndWellbeingFormData(passportIdData);
            setProfileMoods(passportIdData.profile_mood || []);
            setPassportData(updatedPassportData as Passport);

            // Sync with Rasa bot
            const rasaUtils = new RasaUtils(RASA_HOST);
            const rasaContext = await rasaUtils.retrieveContext(attrs.sub, session.accessToken);
            if (rasaContext.sender_id) {
                await rasaUtils.saveContext(rasaContext);
            }

            // Set authentication complete
            setIsAuthenticating(false);
        } catch (error) {
            console.error("Error initializing user data:", error);
            setIsAuthenticating(false);
        }
    }, []);

    /**
     * Listens for authentication events (Sign-in, Sign-out, etc.).
     */
    useEffect(() => {
        Hub.listen("auth", ({ payload: { event, data } }) => {
            switch (event) {
                case "signIn":
                case "autoSignIn":
                case "cognitoHostedUI":
                    Analytics.record({
                        name: "UserSignIn",
                        attributes: { email: data.attributes.email }
                    });
                    onLoggedIn(data, data.signInUserSession.accessToken);
                    break;
                case "signOut":
                    setUser(null);
                    break;
            }
        });
    }, [initUserData]);

    return (
      <AuthContext.Provider
        value={{
            user,
            hasAccess,
            isAuthenticating,
            passportData: passportData as Passport,
            login: signIn,
            logout: signOut,
            profileMoods: profileMoods as IProfileMood[],
            setPassportData,
            setProfileMoods,
            accessLink,
            accessErrorMessage,
        }}
      >
          {children}
      </AuthContext.Provider>
    );
};

export default AuthProvider;